import { FormControl, ValidatorFn, AsyncValidatorFn, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';

// ============================================================
// 1. TIPOS GENÉRICOS PARA FORMULARIOS TIPADOS
// ============================================================

/**
 * Convierte cada propiedad de T en FormControl<T[K] | null>
 */
export type FormControlsFor<T> = {
    [K in keyof T]:
    // Si el valor es un arreglo, usamos FormArray (declaración genérica)
    T[K] extends Array<any> ? FormArray :
    // Si el valor es un objeto (subformulario), devolvemos TypedFormGroup
    T[K] extends Record<string, any> ? TypedFormGroup<T[K]> :
    // En cualquier otro caso, FormControl normal
    FormControl<T[K] | null>;
};

/**
 * Tipo para valores crudos del formulario
 */
export type RawValueFor<T> = {
    [K in keyof T]: T[K] | null;
};

/**
 * Tipo para valores parciales (después de validación)
 */
export type ValidValueFor<T> = {
    [K in keyof T]?: T[K];
};

// ============================================================
// 2. CONFIGURACIÓN DE CAMPOS
// ============================================================

/**
 * Configuración de validadores para un campo
 */
export interface FieldValidatorsConfig {
    validators?: ValidatorFn[];
    asyncValidators?: AsyncValidatorFn[];
}

// ============================================================
// CONFIGURACIÓN DE CAMPOS - TIPOS DISCRIMINADOS
// ============================================================

/**
 * Configuración base común a todos los campos
 */
export interface BaseFieldConfig<T = any> {
    /** Label a mostrar en la UI */
    label?: string;

    /** Valor inicial */
    initialValue?: T;

    /** Si el campo es requerido */
    required?: boolean;

    /** Habilitar/deshabilitar campo */
    disabled?: boolean;

    /** Descripción o hint para el usuario */
    description?: string;

    /** Mensajes de error personalizados para este campo.
     *  Cada clave corresponde al nombre del error que puede
     *  devolver un validador (required, pattern, futureDateNotAllowed, etc.).
     *  Se utiliza cuando se presenta el mensaje en la vista.
     *  Ejemplo:
     *    errorMessages: { required: 'Obligatorio', pattern: 'Formato inválido' }
     */
    errorMessages?: Record<string, string>;

    /** Metadata adicional */
    metadata?: Record<string, any>;

    /** Mostrar solo en ciertos casos */
    hidden?: boolean;

    /** Help text adicional */
    helpText?: string;
}

/**
 * Configuración para campos simples (text, number, email, etc)
 */
export interface SimpleFieldConfig<T = any> extends BaseFieldConfig<T> {
    /** Tipo de control simple */
    type: 'text' | 'number' | 'email' | 'password' | 'checkbox' | 'textarea' | 'date' | 'custom';

    /** Placeholder del input */
    placeholder?: string;

    /** Validadores síncronos */
    validators?: ValidatorFn[];

    /** Validadores asíncronos */
    asyncValidators?: AsyncValidatorFn[];
}

/**
 * Configuración para campos select (opciones predefinidas)
 */
export interface SelectFieldConfig<T = any> extends BaseFieldConfig<T> {
    /** Tipo de control select */
    type: 'select';

    /** Placeholder del select */
    placeholder?: string;

    /** Opciones disponibles */
    options: { label: string; value: T }[];

    /** Validadores síncronos */
    validators?: ValidatorFn[];

    /** Validadores asíncronos */
    asyncValidators?: AsyncValidatorFn[];
}

/**
 * Configuración para formularios anidados (FormGroup)
 * 
 * Permite alojar otro FormConfig dentro de este campo,
 * creando una estructura jerárquica de formularios.
 * 
 * Ejemplo:
 * ```typescript
 * {
 *   type: 'group',
 *   label: 'Crear Nuevo Producto',
 *   nestedFormConfig: TYRE_PRODUCT_FORM_CONFIG
 * }
 * ```
 */
export interface GroupFieldConfig<T = any> extends BaseFieldConfig<T> {
    /** Tipo de control group */
    type: 'group';

    /** Configuración del formulario anidado */
    nestedFormConfig: FormConfig<any>;

    /** Validadores a nivel del grupo */
    validators?: ValidatorFn[];

    /** Validadores asíncronos a nivel del grupo */
    asyncValidators?: AsyncValidatorFn[];
}

/**
 * Configuración para arrays de items (FormArray)
 * 
 * Permite definir un array de items, donde cada item
 * sigue la estructura definida en itemFormConfig.
 * 
 * Ejemplo:
 * ```typescript
 * {
 *   type: 'array',
 *   label: 'Líneas de Orden',
 *   itemFormConfig: ORDER_LINE_FORM_CONFIG
 * }
 * ```
 */
export interface ArrayFieldConfig<T = any> extends BaseFieldConfig<T> {
    /** Tipo de control array */
    type: 'array';

    /** Configuración para cada item del array */
    itemFormConfig: FormConfig<any>;

    /** Validadores a nivel del array */
    validators?: ValidatorFn[];

    /** Validadores asíncronos a nivel del array */
    asyncValidators?: AsyncValidatorFn[];

    /** Mínimo número de items */
    minItems?: number;

    /** Máximo número de items */
    maxItems?: number;
}

/**
 * Tipo discriminado: FieldConfig puede ser cualquiera de sus variantes
 * 
 * Esto permite que TypeScript valide correctamente qué propiedades
 * están disponibles según el tipo de campo.
 * 
 * Ejemplo:
 * ```typescript
 * // ✅ TypeScript sabe que 'options' es válido
 * const selectConfig: FieldConfig = {
 *   type: 'select',
 *   options: [{ label: 'Opción', value: 1 }]
 * };
 * 
 * // ❌ Error: 'options' no existe para tipo 'text'
 * const textConfig: FieldConfig = {
 *   type: 'text',
 *   options: [] // TypeScript error
 * };
 * 
 * // ✅ TypeScript sabe que 'nestedFormConfig' es válido
 * const groupConfig: FieldConfig = {
 *   type: 'group',
 *   nestedFormConfig: SOME_FORM_CONFIG
 * };
 * ```
 */
export type FieldConfig<T = any> =
    | SimpleFieldConfig<T>
    | SelectFieldConfig<T>
    | GroupFieldConfig<T>
    | ArrayFieldConfig<T>;

// ============================================================
// REGLAS CONDICIONALES DINÁMICAS
// ============================================================

/**
 * Acciones que se pueden ejecutar en un campo según reglas condicionales
 */
export type FormAction =
    | { action: 'disable'; fields: string[] }
    | { action: 'enable'; fields: string[] }
    | { action: 'show'; fields: string[] }
    | { action: 'hide'; fields: string[] }
    | { action: 'require'; fields: string[] }
    | { action: 'unrequire'; fields: string[] }
    | { action: 'clear'; fields: string[] };

/**
 * Regla condicional para controlar dinámicamente el estado de campos
 * 
 * Permite definir dependencias entre campos donde el estado de unos
 * afecta la visibilidad, validación o habilitación de otros.
 * 
 * Ejemplo:
 * ```typescript
 * {
 *   dependsOn: ['productId'],
 *   condition: (values) => !!values.productId,
 *   thenActions: [
 *     { action: 'disable', fields: ['newProduct', 'productSearch'] },
 *     { action: 'clear', fields: ['productSearch'] }
 *   ],
 *   elseActions: [
 *     { action: 'enable', fields: ['newProduct', 'productSearch'] }
 *   ]
 * }
 * ```
 */
export interface ConditionalRule<T = any> {
    /**
     * Campo(s) que desencadenan esta regla
     * Cuando alguno de estos campos cambia, se evalúa la condición
     */
    dependsOn: (keyof T)[];

    /**
     * Función que evalúa la condición
     * Retorna true si se debe aplicar la acción (thenActions)
     * Retorna false si se debe aplicar elseActions
     */
    condition: (formValues: Partial<T>) => boolean;

    /**
     * Acciones a ejecutar cuando la condición es verdadera
     */
    thenActions: FormAction[];

    /**
     * Acciones a ejecutar cuando la condición es falsa (opcional)
     */
    elseActions?: FormAction[];
}

/**
 * Configuración completa del formulario
 * 
 * Define la estructura, validadores y metadatos de un formulario tipado.
 * Usa tipos discriminados para los campos, permitiendo anidamiento
 * de formularios y arrays de items.
 * 
 * Ejemplo:
 * ```typescript
 * export const USER_FORM_CONFIG: FormConfig<User> = {
 *   name: 'userForm',
 *   fields: {
 *     name: {
 *       type: 'text',
 *       label: 'Nombre',
 *       required: true,
 *       validators: [Validators.minLength(3)]
 *     },
 *     email: {
 *       type: 'email',
 *       label: 'Email',
 *       required: true
 *     },
 *     profile: {
 *       type: 'group',
 *       label: 'Perfil',
 *       nestedFormConfig: PROFILE_FORM_CONFIG  // ← Anidado
 *     },
 *     preferences: {
 *       type: 'array',
 *       label: 'Preferencias',
 *       itemFormConfig: PREFERENCE_FORM_CONFIG  // ← Array de items
 *     }
 *   }
 * };
 * ```
 */
export interface FormConfig<T extends Record<string, any>> {
    /** Nombre único del formulario */
    name: string;

    /** Descripción del formulario */
    description?: string;

    /** Configuración de cada campo (tipada) */
    fields: {
        [K in keyof T]: FieldConfig<T[K]>;
    };

    /** Validadores a nivel de formulario completo */
    formValidators?: ValidatorFn[];

    /** Validadores asíncronos a nivel de formulario */
    formAsyncValidators?: AsyncValidatorFn[];

    /**
     * Reglas condicionales que se aplican automáticamente
     * 
     * Permite definir lógica dinámica sin tocar los componentes.
     * Las reglas se evaluán automáticamente cuando los campos monitoreados cambian.
     * 
     * Ejemplo:
     * ```typescript
     * conditionalRules: [
     *   {
     *     dependsOn: ['productId'],
     *     condition: (values) => !!values.productId,
     *     thenActions: [
     *       { action: 'disable', fields: ['newProduct', 'productSearch'] },
     *       { action: 'clear', fields: ['productSearch'] }
     *     ],
     *     elseActions: [
     *       { action: 'enable', fields: ['newProduct', 'productSearch'] }
     *     ]
     *   }
     * ]
     * ```
     */
    conditionalRules?: ConditionalRule<T>[];

    /** Metadata adicional para documentación o configuración.
     *  Puede incluir mensajes de error a nivel de formulario completo que se
     *  muestran cuando la validación global (formValidators) falla.
     *
     *  Ejemplo:
     *    metadata: {
     *      errorMessages: { global: 'Revise los campos obligatorios' }
     *    }
     */
    metadata?: {
        /** Categoría o módulo al que pertenece */
        category?: string;

        /** Tipo específico de formulario */
        type?: string;

        /** Versión de la configuración */
        version?: string;

        /** Fecha de última actualización */
        lastUpdated?: Date;

        /** Tags para búsqueda y organización */
        tags?: string[];

        /** Mensajes de error globales (p.ej. para validadores de formulario).
         *  Las claves suelen ser `global` o el nombre del validador.
         */
        errorMessages?: { [key: string]: string };

        /** Documentación adicional */
        documentation?: {
            purpose?: string;
            workflow?: string[];
            notes?: string[];
            examples?: string[];
        };

        /** Información de auditoría */
        createdBy?: string;
        createdAt?: Date;

        /** Datos adicionales */
        [key: string]: any;
    };
}

// ============================================================
// 3. RESULTADO DE OPERACIONES DEL FORMULARIO
// ============================================================

/**
 * Resultado de validación del formulario completo
 */
export interface FormValidationResult<T> {
    valid: boolean;
    data?: ValidValueFor<T>;
    fieldErrors?: {
        [K in keyof T]?: FieldValidationResult;
    };
    formErrors?: Record<string, any>;
}

// ============================================================
// UTILIDADES PARA TRABAJAR CON FIELDCONFIG DISCRIMINADO
// ============================================================

/**
 * Type guards para verificar el tipo específico de un FieldConfig
 */

// NOT NEEDED

export namespace FieldConfigGuards {
    /**
     * Verificar si un FieldConfig es de tipo simple
     */
    export function isSimpleField(config: FieldConfig): config is SimpleFieldConfig {
        return config.type !== undefined &&
            ['text', 'number', 'email', 'password', 'checkbox', 'textarea', 'date', 'custom'].includes(config.type as string);
    }

    /**
     * Verificar si un FieldConfig es de tipo select
     */
    export function isSelectField(config: FieldConfig): config is SelectFieldConfig {
        return config.type === 'select';
    }

    /**
     * Verificar si un FieldConfig es de tipo group (formulario anidado)
     */
    export function isGroupField(config: FieldConfig): config is GroupFieldConfig {
        return config.type === 'group';
    }

    /**
     * Verificar si un FieldConfig es de tipo array
     */
    export function isArrayField(config: FieldConfig): config is ArrayFieldConfig {
        return config.type === 'array';
    }

    /**
     * Verificar si un FieldConfig tiene formulario anidado
     */
    export function hasNestedForm(config: FieldConfig): config is GroupFieldConfig {
        return isGroupField(config) && 'nestedFormConfig' in config;
    }

    /**
     * Verificar si un FieldConfig es un array con items
     */
    export function isArrayOfItems(config: FieldConfig): config is ArrayFieldConfig {
        return isArrayField(config) && 'itemFormConfig' in config;
    }
}

/**
 * Utilidades para trabajar con FormConfig
 */
export namespace FormConfigUtils {
    /**
     * Obtener todos los campos que contienen formularios anidados
     */
    export function getNestedFormFields<T extends Record<string, any>>(
        config: FormConfig<T>
    ): Array<{ fieldName: keyof T; config: FormConfig<any> }> {
        const nested: Array<{ fieldName: keyof T; config: FormConfig<any> }> = [];

        Object.entries(config.fields).forEach(([fieldName, fieldConfig]) => {
            if (FieldConfigGuards.hasNestedForm(fieldConfig)) {
                nested.push({
                    fieldName: fieldName as keyof T,
                    config: fieldConfig.nestedFormConfig,
                });
            }
        });

        return nested;
    }

    /**
     * Obtener todos los campos que son arrays
     */
    export function getArrayFields<T extends Record<string, any>>(
        config: FormConfig<T>
    ): Array<{ fieldName: keyof T; config: FormConfig<any> }> {
        const arrays: Array<{ fieldName: keyof T; config: FormConfig<any> }> = [];

        Object.entries(config.fields).forEach(([fieldName, fieldConfig]) => {
            if (FieldConfigGuards.isArrayOfItems(fieldConfig)) {
                arrays.push({
                    fieldName: fieldName as keyof T,
                    config: fieldConfig.itemFormConfig,
                });
            }
        });

        return arrays;
    }

    /**
     * Obtener profundidad de anidamiento del formulario
     */
    export function getMaxNestingLevel<T extends Record<string, any>>(
        config: FormConfig<T>
    ): number {
        const nestedForms = getNestedFormFields(config);
        const arrayForms = getArrayFields(config);

        if (nestedForms.length === 0 && arrayForms.length === 0) {
            return 1; // Sin anidamiento
        }

        let maxLevel = 1;

        nestedForms.forEach(({ config: nestedConfig }) => {
            const level = getMaxNestingLevel(nestedConfig);
            maxLevel = Math.max(maxLevel, 1 + level);
        });

        arrayForms.forEach(({ config: itemConfig }) => {
            const level = getMaxNestingLevel(itemConfig);
            maxLevel = Math.max(maxLevel, 1 + level);
        });

        return maxLevel;
    }
}

/**
 * Resultado de validación de un campo
 */
export interface FieldValidationResult {
    valid: boolean;
    errors?: Record<string, any>;
    warnings?: string[];
}

/**
 * Mensaje de error con clave de traducción y parámetros
 */
export interface ErrorMessage {
    key: string;
    params?: Record<string, any>;
}

/**
 * Opciones para crear un formulario
 */
export interface CreateFormOptions<T> {
    /** Valores iniciales */
    initialValue?: Partial<T>;

    /** Si marcar campos como touched automáticamente */
    markTouched?: boolean;

    /** Actualizar valor cuando cambian los datos */
    updateValueAndValidity?: boolean;

    /** Emitir eventos */
    emitEvent?: boolean;
}

/**
 * Evento de cambio en el formulario
 */
export interface FormChangeEvent<T> {
    type: 'valueChanged' | 'statusChanged' | 'fieldChanged';
    fieldName?: keyof T;
    value?: any;
    status?: 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED';
    timestamp: number;
}

// ============================================================
// 4. ESTADO DEL FORMULARIO
// ============================================================

/**
 * Estado del formulario para debugging y logging
 */
export interface FormState<T> {
    isDirty: boolean;
    isTouched: boolean;
    isPending: boolean;
    isValid: boolean;
    isDisabled: boolean;
    errors: Record<string, any>;
    value: Partial<T>;
    rawValue: RawValueFor<T>;
}

// ============================================================
// 5. SERVICIOS Y OPERACIONES
// ============================================================

/**
 * Interfaz para servicios que crean formularios
 */
export interface IFormBuilder<T extends Record<string, any>> {
    /**
     * Crear un formulario basado en una configuración
     */
    create(config: FormConfig<T>, options?: CreateFormOptions<T>): TypedFormGroup<T>;

    /**
     * Validar un formulario
     */
    validate(form: TypedFormGroup<T>): FormValidationResult<T>;

    /**
     * Reset del formulario
     */
    reset(form: TypedFormGroup<T>, value?: Partial<T>): void;

    /**
     * Converter formulario a objeto tipado
     */
    toEntity(form: TypedFormGroup<T>): Partial<T>;
}

// ============================================================
// 6. CLASE PRINCIPAL: TypedFormGroup
// ============================================================

/**
 * FormGroup tipado con acceso seguro a controles
 * Extiende FormGroup de Angular con tipado completo
 */
export class TypedFormGroup<T extends Record<string, any>> extends FormGroup {
    private readonly _formGroup: any; // FormGroup
    _config?: FormConfig<T>;
    private _changeListeners: Array<(event: FormChangeEvent<T>) => void> = [];

    // ========== PROPIEDADES PÚBLICAS ==========

    /**
     * Los controles del formulario, tipados
     */
    declare controls: FormControlsFor<T>;

    /**
     * El valor actual del formulario
     */
    declare value: Partial<RawValueFor<T>>;

    /**
     * Estados del formulario
     */

    // ========== MÉTODOS DE ACCESO A CONTROLES ==========

    /**
     * Obtener un control de forma tipada
     */
    getControl<K extends keyof T>(key: K): FormControl<T[K] | null> {
        return this.controls[key] as FormControl<T[K] | null>;
    }

    /**
     * Obtener el valor de un control
     */
    getControlValue<K extends keyof T>(key: K): T[K] | null {
        return this.controls[key].value as T[K] | null;
    }

    /**
     * Establecer el valor de un control
     */
    setControlValue<K extends keyof T>(key: K, value: T[K] | null): void {
        // control puede ser FormControl, FormGroup o FormArray según T[K]
        (this.controls[key] as any).setValue(value);
    }

    /**
     * Parchear el valor de un control
     */
    patchControlValue<K extends keyof T>(
        key: K,
        value: T[K] | null
    ): void {
        (this.controls[key] as any).patchValue(value);
    }

    /**
     * Verificar si un control es válido
     */
    isControlValid<K extends keyof T>(key: K): boolean {
        return this.controls[key].valid;
    }

    /**
     * Verificar si un control tiene errores
     */
    hasControlError<K extends keyof T>(
        key: K,
        errorName?: string
    ): boolean {
        const control = this.controls[key];
        if (!control.errors) return false;
        return errorName ? errorName in control.errors : true;
    }

    /**
     * Obtener errores de un control
     */
    getControlErrors<K extends keyof T>(key: K): Record<string, any> | null {
        return this.controls[key].errors;
    }

    /**
     * Deshabilitar un control
     */
    disableControl<K extends keyof T>(key: K): void {
        this.controls[key].disable();
    }

    /**
     * Habilitar un control
     */
    enableControl<K extends keyof T>(key: K): void {
        this.controls[key].enable();
    }

    /**
     * Marcar como touched
     */
    markControlTouched<K extends keyof T>(key: K): void {
        this.controls[key].markAsTouched();
    }

    /**
     * Marcar como untouched
     */
    markControlUntouched<K extends keyof T>(key: K): void {
        this.controls[key].markAsUntouched();
    }

    // ========== MÉTODOS DE VALIDACIÓN ==========

    /**
     * Validar el formulario completo
     */
    validate(): FormValidationResult<T> {
        return {
            valid: this.valid,
            data: this.getValidValues(),
            formErrors: this.errors || undefined,
        };
    }

    /**
     * Obtener solo valores válidos (sin nulos)
     */
    getValidValues(): ValidValueFor<T> {
        const result: any = {};
        Object.keys(this.controls).forEach((key) => {
            const control = this.controls[key as keyof T];
            if (control.valid && control.value !== null) {
                result[key] = control.value;
            }
        });
        return result as ValidValueFor<T>;
    }

    /**
     * Obtener el estado actual del formulario
     */
    getState(): FormState<T> {
        return {
            isDirty: this.dirty,
            isTouched: this.touched,
            isPending: this.pending,
            isValid: this.valid,
            isDisabled: this.disabled,
            errors: this.errors || {},
            value: this.value as any,
            rawValue: this.getRawValue(),
        };
    }

    // ========== MÉTODOS DE MANIPULACIÓN ==========

    /**
     * Suscribirse a eventos personalizados
     */
    onChange(listener: (event: FormChangeEvent<T>) => void): () => void {
        this._changeListeners.push(listener);
        return () => {
            this._changeListeners = this._changeListeners.filter((l) => l !== listener);
        };
    }

    /**
     * Disparar evento de cambio
     */
    protected emitChange(event: FormChangeEvent<T>): void {
        this._changeListeners.forEach((listener) => listener(event));
    }

    /**
     * Convertir a entidad del dominio
     */
    toEntity(): ValidValueFor<T> {
        return this.getValidValues();
    }

    /**
     * Retorna un mensaje de error para el campo especificado.
     * Devuelve un objeto con la clave de traducción y parámetros dinámicos.
     */
    getFieldErrorMessage<K extends keyof T>(
        control: FormControl,
        fieldName?: K
    ): ErrorMessage | null {
        if (!control?.errors) return null;
        const errs = control.errors as Record<string, any>;

        // intentar averiguar el nombre del campo si no se indicó
        let key: keyof T | undefined = fieldName;
        if (!key) {
            for (const k in this.controls) {
                if (this.controls[k] === control) {
                    key = k as keyof T;
                    break;
                }
            }
        }

        const fieldCfg = key ? (this._config?.fields[key] as any) : undefined;
        const msgs: Record<string, string> = fieldCfg?.errorMessages || {};

        for (const errKey of Object.keys(errs)) {
            if (msgs[errKey]) {
                // Si hay mensaje personalizado, asumimos que es una clave de traducción sin params
                return { key: msgs[errKey] };
            }
            switch (errKey) {
                case 'required':
                    return { key: 'validation.required' };
                case 'min':
                    return { key: 'validation.min', params: { value: errs['min'].min } };
                case 'max':
                    return { key: 'validation.max', params: { value: errs['max'].max } };
                case 'minlength':
                    return { key: 'validation.minlength', params: { value: errs['minlength'].requiredLength } };
                case 'maxlength':
                    return { key: 'validation.maxlength', params: { value: errs['maxlength'].requiredLength } };
                case 'pattern':
                    return { key: 'validation.pattern', params: { message: errs['pattern'].message } };
                default:
                    break;
            }
        }
        return { key: 'validation.invalid' };
    }

    /** Devuelve mensaje global (metadata.errorMessages.global) o null */
    getFormErrorMessage(): string | null {
        return this._config?.metadata?.errorMessages?.['global'] || null;
    }

    // ========== MÉTODOS DE REGLAS CONDICIONALES DINÁMICAS ==========

    /**
     * Aplica las reglas condicionales del formulario
     * Se llama automáticamente desde FormBuilderService
     * 
     * Gestiona automáticamente:
     * - Habilitar/Deshabilitar campos
     * - Mostrar/Ocultar campos
     * - Requerir/No requerir campos
     * - Limpiar valores
     * 
     * @param rules Reglas condicionales a aplicar
     * @returns Función para desuscribirse de todas las reglas
     */
    applyConditionalRules(rules: ConditionalRule<T>[] | undefined): () => void {
        if (!rules || rules.length === 0) {
            return () => { };
        }

        const unsubscribers: Array<() => void> = [];

        rules.forEach((rule) => {
            // Ejecutar regla inicial
            this.evaluateAndApplyRule(rule);

            // Crear subscripciones para cada campo de dependencia
            rule.dependsOn.forEach((fieldName) => {
                const control = this.get(fieldName as string);
                if (control) {
                    const subscription = control.valueChanges.subscribe(() => {
                        this.evaluateAndApplyRule(rule);
                    });
                    unsubscribers.push(() => subscription.unsubscribe());
                }
            });
        });

        return () => unsubscribers.forEach((unsub) => unsub());
    }

    /**
     * Evalúa una regla y aplica las acciones correspondientes
     * 
     * Soporta campos simples (ej: 'name') y anidados (ej: 'newProduct.name')
     * 
     * @param rule Regla a evaluar
     */
    private evaluateAndApplyRule(rule: ConditionalRule<T>): void {
        try {
            const conditionMet = rule.condition(this.value as Partial<T>);
            const actions = conditionMet ? rule.thenActions : rule.elseActions;

            if (!actions) return;

            actions.forEach((action) => {
                switch (action.action) {
                    case 'disable':
                        action.fields.forEach((fieldPath) => {
                            const control = this.getNestedControl(fieldPath);
                            if (control) control.disable();
                        });
                        break;

                    case 'enable':
                        action.fields.forEach((fieldPath) => {
                            const control = this.getNestedControl(fieldPath);
                            if (control) control.enable();
                        });
                        break;

                    case 'show':
                        action.fields.forEach((field) => {
                            const config = this._config?.fields[field as keyof T];
                            if (config) config.hidden = false;
                        });
                        break;

                    case 'hide':
                        action.fields.forEach((field) => {
                            const config = this._config?.fields[field as keyof T];
                            if (config) config.hidden = true;
                        });
                        break;

                    case 'require':
                        action.fields.forEach((fieldPath) => {
                            const control = this.getNestedControl(fieldPath);
                            if (control) {
                                const validators = [Validators.required];
                                if (control.validator) {
                                    validators.push(control.validator);
                                }
                                control.setValidators(validators);
                                control.updateValueAndValidity();
                            }
                        });
                        break;

                    case 'unrequire':
                        action.fields.forEach((fieldPath) => {
                            const control = this.getNestedControl(fieldPath);
                            if (control) {
                                const validators = control.validator ? [control.validator] : [];
                                control.setValidators(validators);
                                control.updateValueAndValidity();
                            }
                        });
                        break;

                    case 'clear':
                        action.fields.forEach((fieldPath) => {
                            const control = this.getNestedControl(fieldPath);
                            if (control) {
                                control.setValue(null);
                            }
                        });
                        break;
                }
            });
        } catch (error) {
            console.error('Error evaluando regla condicional:', error);
        }
    }

    /**
     * Obtiene un control usando notación de punto para campos anidados
     * 
     * Soporta:
     * - Campos simples: 'name' → this.get('name')
     * - Campos anidados: 'newProduct.name' → this.get('newProduct').get('name')
     * - Múltiples niveles: 'profile.address.street'
     * 
     * @param path Ruta del control (ej: 'newProduct.name')
     * @returns El control encontrado o null
     */
    private getNestedControl(path: string): AbstractControl | null {
        const parts = path.split('.');
        let control: AbstractControl | null = this;

        for (const part of parts) {
            if (control instanceof FormGroup) {
                control = control.get(part);
            } else {
                return null;
            }
        }

        return control;
    }

    // ========== MÉTODOS PARA FORMULARIOS ANIDADOS ==========

    /**
     * Obtener un FormGroup anidado de forma tipada
     * 
     * Utilidad para acceder a formularios anidados dentro de este formulario.
     * 
     * Ejemplo:
     * ```typescript
     * const nestedForm = this.form.getNestedFormGroup('newProduct');
     * nestedForm.patchValue({ width: 225 });
     * ```
     */
    getNestedFormGroup<K extends keyof T>(key: K): TypedFormGroup<any> | null {
        const control = this.controls[key];
        if (control && 'controls' in control) {
            // Es un FormGroup
            return control as any;
        }
        return null;
    }

    /**
     * Obtener información de todos los formularios anidados
     * 
     * Retorna un mapeo de qué campos contienen formularios anidados
     * y su configuración.
     */
    getNestedFormsInfo(): Map<keyof T, FormConfig<any>> {
        const result = new Map<keyof T, FormConfig<any>>();

        if (!this._config) return result;

        const nestedForms = FormConfigUtils.getNestedFormFields(this._config);
        nestedForms.forEach(({ fieldName, config }) => {
            result.set(fieldName, config);
        });

        return result;
    }

    /**
     * Obtener información de todos los arrays
     * 
     * Retorna un mapeo de qué campos son arrays y su configuración de items.
     */
    getArrayFieldsInfo(): Map<keyof T, FormConfig<any>> {
        const result = new Map<keyof T, FormConfig<any>>();

        if (!this._config) return result;

        const arrayFields = FormConfigUtils.getArrayFields(this._config);
        arrayFields.forEach(({ fieldName, config }) => {
            result.set(fieldName, config);
        });

        return result;
    }

    /**
     * Obtener la profundidad máxima de anidamiento
     * 
     * Útil para debugging y análisis de la estructura del formulario.
     */
    getMaxNestingLevel(): number {
        if (!this._config) return 1;
        return FormConfigUtils.getMaxNestingLevel(this._config);
    }

    /**
     * Validar que al menos uno de los campos anidados es válido
     * 
     * Ejemplo:
     * ```typescript
     * if (this.form.isAnyNestedFormValid(['profile', 'settings'])) {
     *   // Au menos uno es válido
     * }
     * ```
     */
    isAnyNestedFormValid(fieldNames: (keyof T)[]): boolean {
        return fieldNames.some((fieldName) => {
            const nested = this.getNestedFormGroup(fieldName);
            return nested?.valid ?? false;
        });
    }

    /**
     * Sincronizar un formulario anidado con este formulario
     * 
     * Cuando el formulario anidado cambia, automáticamente
     * actualiza el valor en este formulario.
     * 
     * Ejemplo:
     * ```typescript
     * const nestedForm = this.formBuilder.create<TyreProduct>(TYRE_CONFIG);
     * this.form.syncNestedForm('newProduct', nestedForm);
     * ```
     */
    syncNestedForm<K extends keyof T>(
        fieldName: K,
        nestedForm: TypedFormGroup<any>
    ): () => void {
        const subscription = nestedForm.valueChanges.subscribe((value) => {
            this.patchValue({ [fieldName]: value } as any);
        });

        // Retornar función para desuscribirse
        return () => subscription.unsubscribe();
    }
}
